<template>
  <div :class="{ 'b-t b-grey-70': index !== 0 || showFirstStoreBorder }">
    <define-store-address>
      <p>
        {{ store.address1 }}<br>
        <template v-if="store.address2">
          {{ store.address2 }}<br>
        </template>
        {{ store.city }} {{ store.state }}. {{ store.postalcode }}, {{ store.country }}<br>
      </p>
    </define-store-address>
    <div class="relative">
      <base-button
        v-if="enableSelection"
        :aria-label="store.name"
        :aria-pressed="isSelected"
        class="absolute mt-1 p-3 ml-3 mt-2"
        @click="onSelectStore"
      >
        <span
          class="block rounded-full ring ring-grey-40 ring-inset duration peer-focus-within:ring-grey-10 peer-focus-visible:outline-auto !h-4 !w-4"
          :class="storeSelectionIndicator"
        >
          <span
            class="block full rounded-full duration bg-brand-1"
            :class="isSelected ? 'op-100' : 'op-0'"
          />
        </span>
      </base-button>
      <base-collapse
        class="w-full"
        :class-toggle="[
          'w-full py-4 px-6',
          { 'pl-14 ': enableSelection },
        ]"
      >
        <template #toggle="{ expanded }">
          <span class="flex gap-4 between">
            <span v-html="store.name" :class="storeName" />
            <vf-icon :dir="expanded ? 'up' : 'down'" name="chevron" size="md" />
          </span>
          <span class="flex text-sm between" :class="storeSubtitle">
            <slot name="store-body-collapsed" :store>
              <store-address v-if="showAddressPreview" />
              <template v-else>
                <span v-if="qty || ignoreInventoryCheck">{{ $t.readyIn24hours }}.</span>
                <span v-else-if="store.sts_enabled === '1'">{{ $t.readyIn3Days }}.</span>
              </template>
              <span>({{ store._distance }} {{ store._distanceuom }})</span>
            </slot>
          </span>
          <span v-if="qty && qty < qtyThreshold" class="text-sm" :class="stockStatus">
            {{ replaceAll($t.qtyLimitLow, { limit: qty }) }}
          </span>
        </template>
        <div
          class="pb-4 space-y-6 px-6"
          :class=" { 'pl-14 ': enableSelection }"
        >
          <address class="text-sm space-y-4">
            <store-address v-if="!showAddressPreview" :class="addressPreview" />
            <ul :class="storeLocationAndPhone">
              <li v-if="store.latitude && store.longitude" class="flex items-center gap-2">
                <vf-icon name="location" :size="iconSize" />
                <vf-link :to="`http://maps.google.com/?q=${store.latitude},${store.longitude}`">
                  {{ $t.getDirections }}
                </vf-link>
              </li>
              <li v-if="store.phone" class="flex items-center gap-2">
                <vf-icon name="phone" :size="iconSize" />
                <vf-link :to="`tel:${store.phone}`">
                  <span class="sr-only">{{ $t.call }}</span>
                  {{ store.phone }}
                </vf-link>
              </li>
            </ul>
          </address>
          <div>
            <p :class="storeHoursHeading">
              {{ $t.storeHours }}
            </p>
            <ul :class="storeHours">
              <li v-if="hasGroupedWeekdays">
                {{ $t.weekday }}: {{ storeHoursByDay.monday }}
              </li>
              <template v-else>
                <li>{{ $t.monday }}: {{ storeHoursByDay.monday }}</li>
                <li>{{ $t.tuesday }}: {{ storeHoursByDay.tuesday }}</li>
                <li>{{ $t.wednesday }}: {{ storeHoursByDay.wednesday }}</li>
                <li>{{ $t.thursday }}: {{ storeHoursByDay.thursday }}</li>
                <li>{{ $t.friday }}: {{ storeHoursByDay.friday }}</li>
              </template>
              <li>{{ $t.saturday }}: {{ storeHoursByDay.saturday }}</li>
              <li>{{ $t.sunday }}: {{ storeHoursByDay.sunday }}</li>
            </ul>
          </div>
          <div v-if="store.store_description && showStoreDescription">
            <p :class="storeHoursHeading">
              {{ $t.getStoreInstruction }}
            </p>
            <p :class="storeHours">
              {{ store.store_description }}
            </p>
          </div>
          <vf-button
            v-if="$feature.allowProductReservationOnPDP && !isReservationOpen && qty! > 0 && !enableReserveDialog"
            :loading="isReservationOutgoing"
            @click="handleReserve"
          >
            {{ $t.reserve }}
          </vf-button>
        </div>
        <form-robis
          v-if="isReservationOpen && !enableReserveDialog"
          ref="robisFormRef"
          :cart-id
          class="mt-2 pb-6"
          :product-title="item?.name"
          :store-shipping-address
          tabindex="-1"
          @success="isReservationOpen = false"
        />
      </base-collapse>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { LocatorSearchItem } from '#types/brandify'
import type { PickupProduct } from '#types/components/product/pickupInStore'

const { item, store, index, selectedStoreId } = defineProps<{
  item?: PickupProduct
  store: LocatorSearchItem & { storeInventory?: number }
  enableSelection?: boolean
  showAddressPreview?: boolean
  selectedStoreId?: string
  index?: number
}>()

const emit = defineEmits<{
  error: [value: boolean]
  select: [storeId: string]
}>()

const [DefineStoreAddress, StoreAddress] = createReusableTemplate()

const selectedStore = defineModel<LocatorSearchItem>()
const isSelected = computed(() => store.storeid === (selectedStoreId || selectedStore.value?.storeid))

const robisFormRef = ref()

const {
  brandClasses: {
    addressPreview,
    storeLocationAndPhone,
    storeName,
    storeHours,
    storeHoursHeading,
    storeSelectionIndicator,
    stockStatus,
    storeSubtitle
  },
  iconSize,
  qtyThreshold,
  showFirstStoreBorder,
  showStoreDescription
} = useAppConfig().components.form.bopisStoreSearch
const { enableReserveDialog } = useAppConfig().components.dialog.pickupInStore
const { $feature } = useNuxtApp()

const { DialogPickupInStoreReserve } = useDialogsStore()
const ignoreInventoryCheck = inject('ignoreInventoryCheck', false)

const dayMappings = [
  ['monday', 'm'],
  ['tuesday', 't'],
  ['wednesday', 'w'],
  ['thursday', 'thu'],
  ['friday', 'f'],
  ['saturday', 'sa'],
  ['sunday', 'su']
] as const

const qty = computed(() => store.quantity || store.storeInventory)

const storeHoursByDay = computed(() => Object.fromEntries<string>(dayMappings.map(([day, fallback]) => {
  const open = store[`${day}_hours_open`]
  const close = store[`${day}_hours_closed`]

  return [day, open && close ? `${open} - ${close}` : store[fallback] || 'N/A']
})))

const hasGroupedWeekdays = computed(() =>
  dayMappings.slice(1, 5)
    .every(([day]) => storeHoursByDay.value[day] === storeHoursByDay.value.monday)
)

let cartId: ReturnType<typeof useProductReservation>['cartId']
let isReservationOpen: ReturnType<typeof useProductReservation>['isReservationOpen']
let isReservationOutgoing: ReturnType<typeof useProductReservation>['isReservationOutgoing']
let reserve: ReturnType<typeof useProductReservation>['reserve']
let storeShippingAddress: ReturnType<typeof useProductReservation>['storeShippingAddress']

if (item && $feature.allowProductReservationOnPDP) {
  ({
    cartId,
    isReservationOpen,
    isReservationOutgoing,
    reserve,
    storeShippingAddress
  } = useProductReservation({ item, store }))
}

const handleReserve = async () => {
  try {
    if (enableReserveDialog) {
      await reserve?.()
      DialogPickupInStoreReserve.open({
        cartId: cartId.value,
        storeShippingAddress: storeShippingAddress.value
      })
      return
    }
    await reserve?.()
    // move focus to form once it appears
    robisFormRef.value.$el.focus()
  }
  catch (err) {
    log.error(`Failed to handle reservation: ${err}`)
    emit('error', true)
  }
}

const onSelectStore = () => {
  emit('select', store.storeid)
  selectedStore.value = store
}

defineExpose({ openRobisForm: handleReserve, storeid: store.storeid })
</script>
